import IconButton from '@mui/material/IconButton';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalyticsService } from 'services/analyticsService';
import { getGeoService } from 'services/geoService';

import { ThemeType } from '../../../context/types';
import { useAddParamsAndNavigate } from '../../../hooks/useAddParamsAndNavigate';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import styles from './placesAutocomplete.module.scss';

interface PlacesAutocompleteProps {
  input: string;
  setSearchResults: (results: any) => void;
  onSelect: (location: any) => void;
}

/**
 * PlacesAutocomplete component provides an input field for searching addresses
 * and displays the search results in a dropdown list. It allows users to select
 * a location from the search results, which then updates the URL parameters and
 * tracks the selection using an analytics service.
 *
 * @component
 * @param {PlacesAutocompleteProps} props - The properties for the component.
 * @param {string} props.input - The input string for searching addresses.
 * @param {Function} props.setSearchResults - The function to set the search results count.
 *
 * @returns {JSX.Element | null} The rendered component or null if the input is empty.
 *
 * @example
 * <PlacesAutocomplete
 *   input="1600 Amphitheatre Parkway"
 *   setSearchResults={(count) => console.log(count)}
 * />
 */
const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  input,
  setSearchResults,
  onSelect,
}) => {
  const geoService = useMemo(() => getGeoService(), []);
  const analyticsService = useMemo(() => getAnalyticsService(), []);

  const [searchAddressResults, setSearchAddressResults] = useState<
    Awaited<ReturnType<typeof geoService.searchAddress>> | undefined
  >();

  useEffect(() => {
    if (input !== geoService.lastSearchAddressQuery) {
      geoService.searchAddress(input).then((results) => {
        setSearchAddressResults(results);
        setSearchResults(results?.length);
      });
    }
  }, [input, geoService, setSearchAddressResults, setSearchResults]);

  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = () => setCollapsed(!collapsed);
  const { t } = useTranslation('common');
  const addParamsAndNavigate = useAddParamsAndNavigate();

  /**
   * Handles the selection of a location from the search results.
   *
   * @param searchAddressResult - The result object from the address search.
   *
   * Tracks the event of setting the starting location and the event of selecting a starting location search.
   * Adds parameters to the URL and navigates to the new URL with the selected location details.
   *
   * @param addParamsAndNavigate - Function to add parameters to the URL and navigate.
   * @param analyticsService - Service for tracking analytics events.
   * @param input - The input value from the search field.
   */
  const handleLocationSelect = useCallback(
    (
      // eslint-disable-next-line no-undef
      searchAddressResult: any,
    ) => {
      analyticsService.track(analyticsService.events.SetStartingLocation, {
        address: searchAddressResult?.address,
        lat: searchAddressResult?.geolocation.lat,
        lon: searchAddressResult?.geolocation.lon,
      });
      analyticsService.track(
        analyticsService.events.StartingLocationSearchSelected,
        {
          address: searchAddressResult?.address,
          value: input,
        },
      );
      onSelect(searchAddressResult);
      addParamsAndNavigate([
        { name: 'startingid', value: searchAddressResult.place_id || '' },
        {
          name: 'startingLat',
          value: searchAddressResult.position?.[0].toString() || '',
        },
        {
          name: 'startingLon',
          value: searchAddressResult.position?.[1].toString() || '',
        },
        {
          name: 'startingAddress',
          value: searchAddressResult.address.replace(', USA', ''),
        },
      ]);
    },
    [addParamsAndNavigate, analyticsService, input],
  );

  if (!input || input.length < 1) return null;

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <span className={styles.title}>
          <div className={styles.logo}>
            {t('campusNavigation.form.from')}
            <img src="/images/logos/microsoft.png" alt="Microsoft" />
          </div>
          <IconButton onClick={toggleCollapsed}>
            <Icon icon={collapsed ? IconType.DOWN : IconType.UP} />
          </IconButton>
        </span>
        {!collapsed && (
          <div className={styles.pacContainer}>
            {searchAddressResults &&
              searchAddressResults.map((searchAddressResult: any) => (
                <button
                  key={searchAddressResult.id}
                  className={styles.pacItem}
                  onClick={() => handleLocationSelect(searchAddressResult)}
                >
                  <Icon
                    icon={IconType.LOCATION_OPEN}
                    theme={ThemeType.PRIMARY}
                  />
                  <span className={styles.pacItemQuery}>
                    {searchAddressResult.address.replace(', USA', '')}
                  </span>
                </button>
              ))}
            {!searchAddressResults && (
              <p>{t('campusNavigation.form.noResults')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PlacesAutocomplete);
